import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LogIn, LogOut, ShoppingBag, LayoutDashboard, User, Globe, Check, ChevronDown, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { LogoWordmark, Logo } from "@/components/Logo";
import { useI18n, LANGS } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";

const LANG_NAMES = { pt: "Português", es: "Español", en: "English", fr: "Français" };

function LangSwitcher({ solid }) {
  const { lang, setLang } = useI18n();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="lang-switcher"
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
            solid ? "bg-th-blue/5 text-th-blue hover:bg-th-blue/10" : "bg-white/5 border border-white/10 text-white hover:bg-white/15"}`}>
          <Globe className="w-4 h-4 text-th-gold" />
          <span>{lang.toUpperCase()}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs text-slate-400">Idioma</DropdownMenuLabel>
        {LANGS.map((l) => (
          <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)}
            className="flex items-center justify-between cursor-pointer">
            <span>{LANG_NAMES[l.code]} <span className="text-slate-400 text-xs">({l.label})</span></span>
            {lang === l.code && <Check className="w-4 h-4 text-th-gold" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { t } = useI18n();
  const { user, isAdmin, logout } = useAuth();
  const { visible } = useStore();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const showStore = visible || isAdmin;
  const canMaterials = user && ["super_admin", "national_admin", "diocesan_admin", "community_coordinator"].includes(user.role);

  const navItems = [
    { to: "/quem-somos", label: t("nav_about") },
    { to: "/comunidades", label: t("nav_communities") },
    { to: "/eventos", label: t("nav_events") },
    { to: "/noticias", label: t("nav_news") },
    { to: "/escola", label: t("nav_school") },
    { to: "/rosario-pela-paz", label: t("nav_prayer") },
    { to: "/candidatar-grupo", label: t("nav_apply_group") },
  ];
  if (showStore) navItems.push({ to: "/loja", label: t("nav_store") });

  const handleLogout = async () => { await logout(); navigate("/"); setOpen(false); };

  const NavLinks = ({ mobile }) => (
    <>
      {navItems.map((item) => (
        <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}
          data-testid={`nav-link-${item.to.slice(1)}`}
          className={({ isActive }) =>
            `${mobile ? "text-lg py-2 text-th-blue" : "text-sm text-white/85 hover:text-th-gold whitespace-nowrap"} font-medium transition-colors ${
              isActive ? (mobile ? "text-th-gold" : "text-th-gold") : ""}`}>
          {item.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="min-h-screen flex flex-col bg-th-parchment">
      <header className="sticky top-0 z-50 bg-th-blue/95 backdrop-blur-md border-b border-th-gold/30">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/" data-testid="nav-logo" className="shrink-0"><LogoWordmark light /></Link>

          <nav className="hidden xl:flex items-center gap-6 mx-auto">
            <NavLinks />
          </nav>

          <div className="hidden xl:flex items-center gap-3 shrink-0">
            <LangSwitcher />
            {showStore && (
              <Link to="/loja/carrinho" data-testid="nav-cart" className="relative text-white/85 hover:text-th-gold pl-1">
                <ShoppingBag className="w-5 h-5" />
                {count > 0 && <span className="absolute -top-2 -right-2 bg-th-gold text-th-bluedark text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>}
              </Link>
            )}
            <span className="w-px h-6 bg-white/15" />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="account-menu" className="flex items-center gap-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/15 transition-colors pl-1 pr-2.5 py-1">
                    <span className="w-7 h-7 rounded-full bg-th-gold flex items-center justify-center text-th-bluedark font-display font-bold text-sm">{(user.name || "?")[0]}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-white/70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <DropdownMenuItem asChild data-testid="menu-backoffice" className="cursor-pointer">
                      <Link to="/admin"><LayoutDashboard className="w-4 h-4 mr-2 text-th-gold" />{t("nav_backoffice")}</Link>
                    </DropdownMenuItem>
                  )}
                  {canMaterials && (
                    <DropdownMenuItem asChild data-testid="menu-materials" className="cursor-pointer">
                      <Link to="/materiais"><Wand2 className="w-4 h-4 mr-2 text-th-gold" />{t("nav_materials")}</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem asChild data-testid="menu-myterco" className="cursor-pointer">
                    <Link to="/o-meu-terco"><User className="w-4 h-4 mr-2 text-th-gold" />{t("nav_myterco")}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={handleLogout} className="cursor-pointer text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />{t("nav_logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/entrar" data-testid="nav-login"><Button size="sm" variant="ghost" className="text-white hover:text-th-gold hover:bg-white/10"><LogIn className="w-4 h-4 mr-1" />{t("nav_login")}</Button></Link>
                <Link to="/aderir" data-testid="nav-join"><Button size="sm" className="bg-th-gold text-th-bluedark hover:bg-th-goldlight font-semibold rounded-full px-5 whitespace-nowrap">{t("nav_join")}</Button></Link>
              </>
            )}
          </div>

          <div className="xl:hidden flex items-center gap-3 ml-auto shrink-0">
            {showStore && (
              <Link to="/loja/carrinho" data-testid="nav-cart-mobile" className="relative text-white">
                <ShoppingBag className="w-5 h-5" />
                {count > 0 && <span className="absolute -top-2 -right-2 bg-th-gold text-th-bluedark text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>}
              </Link>
            )}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="text-white"><Menu className="w-6 h-6" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-th-parchment w-[85%] sm:w-96">
                <div className="flex flex-col h-full">
                  <div className="mb-6"><Logo size={48} /></div>
                  <nav className="flex flex-col gap-1"><NavLinks mobile /></nav>
                  <div className="mt-6 pt-6 border-t border-th-gold/30 flex flex-col gap-3">
                    {user ? (
                      <>
                        {isAdmin && <Link to="/admin" onClick={() => setOpen(false)}><Button className="w-full bg-th-blue text-white" data-testid="mobile-backoffice">{t("nav_backoffice")}</Button></Link>}
                        {canMaterials && <Link to="/materiais" onClick={() => setOpen(false)}><Button variant="outline" className="w-full" data-testid="mobile-materials">{t("nav_materials")}</Button></Link>}
                        <Link to="/o-meu-terco" onClick={() => setOpen(false)}><Button variant="outline" className="w-full">{t("nav_myterco")}</Button></Link>
                        <Button onClick={handleLogout} className="w-full bg-th-gold text-th-bluedark" data-testid="mobile-logout">{t("nav_logout")}</Button>
                      </>
                    ) : (
                      <>
                        <Link to="/entrar" onClick={() => setOpen(false)}><Button variant="outline" className="w-full" data-testid="mobile-login">{t("nav_login")}</Button></Link>
                        <Link to="/aderir" onClick={() => setOpen(false)}><Button className="w-full bg-th-gold text-th-bluedark" data-testid="mobile-join">{t("nav_join")}</Button></Link>
                      </>
                    )}
                  </div>
                  <div className="mt-auto pt-6"><LangSwitcher solid /></div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-th-bluedark text-white/80 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <LogoWordmark light />
            <p className="mt-4 text-sm text-white/60 max-w-xs">{t("footer_tagline")}</p>
          </div>
          <div className="text-sm">
            <div className="text-th-gold font-semibold uppercase text-xs tracking-widest mb-3">Menu</div>
            <div className="flex flex-col gap-2">
              <Link to="/quem-somos" className="hover:text-th-gold">{t("nav_about")}</Link>
              <Link to="/comunidades" className="hover:text-th-gold">{t("nav_communities")}</Link>
              <Link to="/eventos" className="hover:text-th-gold">{t("nav_events")}</Link>
              <Link to="/noticias" className="hover:text-th-gold">{t("nav_news")}</Link>
              <Link to="/escola" className="hover:text-th-gold">{t("nav_school")}</Link>
              <Link to="/rosario-pela-paz" className="hover:text-th-gold">{t("nav_prayer")}</Link>
            </div>
          </div>
          <div className="text-sm">
            <div className="text-th-gold font-semibold uppercase text-xs tracking-widest mb-3">Contacto</div>
            <a href="mailto:mta@tercodoshomens.eu" className="text-white/60 hover:text-th-gold" data-testid="footer-contact-email">mta@tercodoshomens.eu</a>
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-white/40">
          © {new Date().getFullYear()} Terço dos Homens Mãe Rainha · Europa
        </div>
      </footer>
    </div>
  );
}
